/**
 * Read a Vite-injected env var in a way Vite can statically inline.
 *
 * Vite only replaces LITERAL `import.meta.env.VITE_KEY` member expressions at
 * build time. Reading through an aliased object (`const e = import.meta.env;
 * e.VITE_KEY`) or a computed key (`import.meta.env[name]`) is NOT inlined and
 * resolves to undefined in production browser bundles — which is exactly the
 * bug class the repo `no-restricted-syntax` guard forbids. Each supported key
 * therefore gets its own literal branch below.
 *
 * The `as any` casts satisfy the SDK's non-Vite tsc and are erased before
 * Vite's replacement runs. The try/catch + process.env fallback keep this
 * usable from Node/Vitest (where `import.meta.env` may be absent) so tests can
 * inject values via `process.env.VITE_*`.
 *
 * Mirrors the local `readEnv` in queries/patient-public.ts (kept separate to
 * avoid re-touching that just-shipped money-path file); both use the same
 * literal-inline shape.
 */
export type ViteEnvKey =
  | 'VITE_MARKETING_REVALIDATE_URL'
  | 'VITE_REVALIDATE_SECRET'
  | 'VITE_STRIPE_PUBLISHABLE_KEY'
  | 'VITE_APP_URL';

export function readViteEnv(name: ViteEnvKey): string | undefined {
  let fromMeta: unknown;
  try {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    fromMeta =
      name === 'VITE_MARKETING_REVALIDATE_URL'
        ? (import.meta as any).env.VITE_MARKETING_REVALIDATE_URL
        : name === 'VITE_REVALIDATE_SECRET'
          ? (import.meta as any).env.VITE_REVALIDATE_SECRET
          : name === 'VITE_STRIPE_PUBLISHABLE_KEY'
            ? (import.meta as any).env.VITE_STRIPE_PUBLISHABLE_KEY
            : name === 'VITE_APP_URL'
              ? (import.meta as any).env.VITE_APP_URL
              : undefined;
    /* eslint-enable @typescript-eslint/no-explicit-any */
  } catch {
    fromMeta = undefined;
  }
  if (typeof fromMeta === 'string' && fromMeta.length > 0) return fromMeta;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const proc: any = (globalThis as any).process;
  const fromProc = proc?.env?.[name];
  if (typeof fromProc === 'string' && fromProc.length > 0) return fromProc;
  return undefined;
}
